export type FetchWithTimeoutOptions = RequestInit & {
  /** Defaults to 500. */
  timeoutMs?: number;
  /**
   * Included in timeout / abort / network error messages.
   * Example: `"makeGetRequest"`.
   */
  debugDescriptor?: string;
  /**
   * Prefix for timeout / abort / network error messages.
   * Defaults to `"Request"`.
   */
  serviceLabel?: string;
};

/**
 * `fetch` with a timeout. Uses `AbortSignal.timeout` when available,
 * otherwise falls back to `AbortController`.
 *
 * Any `signal` passed in `options` is replaced by the timeout signal.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  options: FetchWithTimeoutOptions = {},
): Promise<Response> {
  const {
    timeoutMs = 500,
    debugDescriptor,
    serviceLabel = "Request",
    ...init
  } = options;

  const descriptorSuffix = debugDescriptor ? `: ${debugDescriptor}` : "";

  // AbortSignal.timeout isn't supported in all browsers, so fall back.
  const abortSignalWithTimeout =
    "timeout" in AbortSignal
      ? AbortSignal.timeout
      : (ms: number) => {
          const ctrl = new AbortController();
          // Abort with "TimeoutError" to match native AbortSignal.timeout.
          setTimeout(() => ctrl.abort(new DOMException("TimeoutError")), ms);
          return ctrl.signal;
        };

  try {
    return await fetch(input, {
      ...init,
      signal: abortSignalWithTimeout(timeoutMs),
    });
  } catch (error) {
    // TimeoutError: thrown by native AbortSignal.timeout when timeout exceeded
    if (error instanceof Error && error.name === "TimeoutError") {
      throw new Error(
        `${serviceLabel} timed out in ${timeoutMs}ms${descriptorSuffix}`,
      );
    }
    // AbortError: thrown by fallback AbortController (also a timeout)
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`${serviceLabel} request aborted${descriptorSuffix}`);
    }
    // Network errors (TypeError): DNS, CORS, connection refused, etc.
    if (error instanceof TypeError) {
      throw new Error(`${serviceLabel} network error${descriptorSuffix}`);
    }
    throw error;
  }
}
